import React, { ChangeEvent, useEffect, useState } from 'react';

import { notifications } from '@mantine/notifications';

import {
  useCreateNoteMutation,
  useUpdateNoteMutation
} from '@/store/slices/api/clinicalNotesApiSlice';

import { ClinicalNote, NoteSource } from '@/types/clinicalNote';
import CustomModal from '@/ui/Modal';
import TextareaWrapper from '@/ui/TextAreaWrapper';

import styles from './AddNotesModal.module.scss';

interface AddNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  noteToEdit?: ClinicalNote | null;
}

const AddNotesModal: React.FC<AddNotesModalProps> = ({
  isOpen,
  onClose,
  patientId,
  noteToEdit
}) => {
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);

  const [createNote, { isLoading: isCreating }] = useCreateNoteMutation();
  const [updateNote, { isLoading: isUpdating }] = useUpdateNoteMutation();

  const isEditMode = !!noteToEdit;
  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (noteToEdit) {
      setNoteText(noteToEdit.content);
    } else {
      setNoteText('');
    }
  }, [noteToEdit, isOpen]);

  const handleTextChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    if (value.length <= 255) {
      setNoteText(value);
    }
  };

  const validateNote = (text: string): { valid: boolean; message?: string } => {
    const trimmedText = text.trim();

    if (trimmedText.length === 0) {
      return { valid: false, message: 'Note text cannot be empty' };
    }

    if (trimmedText.length > 255) {
      return { valid: false, message: 'Note text must be 255 characters or less' };
    }

    return { valid: true };
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      if (isEditMode && noteToEdit) {
        await updateNote({
          noteId: noteToEdit.id,
          content: noteText.trim(),
          patientId
        }).unwrap();

        notifications.show({
          title: 'Success',
          message: 'Note updated successfully',
          color: 'green'
        });
      } else {
        await createNote({
          patientId,
          content: noteText.trim(),
          source: NoteSource.INTERNAL
        }).unwrap();

        notifications.show({
          title: 'Success',
          message: 'Note saved successfully',
          color: 'green'
        });
      }

      // Reset form state
      setNoteText('');
      setSaving(false);
      onClose();
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'save'} note:`, error);
      notifications.show({
        title: 'Error',
        message: `Failed to ${isEditMode ? 'update' : 'save'} note. Please try again.`,
        color: 'red'
      });

      setSaving(false);
    }
  };

  const cancelButton = () => {
    return {
      text: 'Cancel',
      onClick: onClose,
      isDisabled: saving || isLoading
    };
  };

  const saveButton = () => {
    const isValidNote = validateNote(noteText).valid;

    return {
      text: isEditMode ? 'Update' : 'Save',
      onClick: handleSave,
      isLoading: saving || isLoading,
      isDisabled: !isValidNote || saving || isLoading
    };
  };

  const modalContent = () => {
    const charactersLeft = 255 - noteText.length;

    return (
      <div className={styles.modalContent}>
        <TextareaWrapper
          placeholder="Text.."
          value={noteText}
          onChange={handleTextChange}
          disabled={saving || isLoading}
        />
        <div className={styles.characterCount}>
          <span className={charactersLeft <= 10 ? styles.warning : ''}>
            {charactersLeft} characters left
          </span>
        </div>
      </div>
    );
  };

  return (
    <CustomModal
      modalProps={{
        opened: isOpen,
        onClose: saving || isLoading ? () => {} : onClose,
        size: 'xl',
        closeOnClickOutside: !(saving || isLoading),
        closeOnEscape: !(saving || isLoading)
      }}
      header={{ title: isEditMode ? 'Edit Note' : 'Add Note' }}
      secondaryButton={cancelButton()}
      primaryButton={saveButton()}>
      {modalContent()}
    </CustomModal>
  );
};

export default AddNotesModal;
